import { useCallback, useEffect, useMemo, useState } from 'react'

import { Box, Skeleton } from '@factorialco/deprecated-design-system'
import {
  WorkflowEmployeeDetailsDocument,
  WorkflowRoleSelectorDocument,
  WorkflowTeamSelectorDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ApproverSelection } from './ApproverSelection'
import { StrategyItem } from './utils/interfaces'
import { getStrategyDisplayInfo } from './utils/strategyDisplayInfoUtils'

interface StrategiesDisplayProps {
  strategies: StrategyItem[]
  onDeleteStrategy: (strategyId: string) => void
}

export const StrategiesDisplay = ({ strategies, onDeleteStrategy }: StrategiesDisplayProps) => {
  const { company } = useCurrentSession()

  const [list, setList] = useState<
    {
      id: string
      label: string
      avatarSrc?: string
      avatarFirstName?: string
      avatarLastName?: string
      isDeactivated?: boolean
    }[]
  >([])

  const employeesIds = useMemo(
    () => strategies.map((strategy) => strategy.employeeId).filter(Boolean),
    [strategies]
  )

  const teamsIds = useMemo(
    () => strategies.map((strategy) => strategy.teamId).filter(Boolean),
    [strategies]
  )

  const rolesIds = useMemo(
    () => strategies.map((strategy) => strategy.roleId).filter(Boolean),
    [strategies]
  )

  const { loading: employeesLoading, data: employeesData } = useQuery(
    WorkflowEmployeeDetailsDocument,
    {
      variables: {
        ids: employeesIds,
      },
      skip: !employeesIds.length,
    }
  )

  const { loading: teamsLoading, data: teamsData } = useQuery(WorkflowTeamSelectorDocument, {
    variables: {
      ids: teamsIds,
    },
    skip: !teamsIds.length,
  })

  const { data: rolesData, loading: rolesLoading } = useQuery(WorkflowRoleSelectorDocument, {
    variables: {
      companyId: company.id,
      uuids: rolesIds,
      ids: rolesIds.map((i) => Number(i)),
    },
    skip: !rolesIds.length,
  })

  useEffect(() => {
    // If no resource is loaded, wait for it
    if (employeesLoading || teamsLoading || rolesLoading) return

    // If resource is loaded, process the strategies
    const employees =
      employeesData?.employees?.employeeNamesConnection?.nodes?.map((emp) => ({
        id: emp.id,
        fullName: emp.fullName,
        avatar: emp.access?.avatar,
        terminated: emp.terminated ?? false,
      })) ?? []
    const teams = (teamsData?.teams?.teamsConnection?.edges ?? []).map((edge) => edge.node)
    const roles = [
      ...(rolesData?.jobCatalog?.rolesConnection?.nodes ?? []),
      ...(rolesData?.jobCatalog?.treeNodesConnection?.nodes ?? []),
    ].map((role) => ({
      id: role.id,
      name: role.name,
      uuid: 'uuid' in role ? role.uuid : role.id.toString(),
      ancestor: null,
    }))

    const newList = strategies.map((strategy) => {
      const displayInfo = getStrategyDisplayInfo({
        strategy,
        employees,
        teams,
        roles,
      })
      return {
        id: strategy.id,
        label: displayInfo.label,
        avatarSrc: displayInfo.avatarSrc,
        avatarFirstName: displayInfo.avatarFirstName,
        avatarLastName: displayInfo.avatarLastName,
        isDeactivated: displayInfo.isDeactivated,
      }
    })

    setList(newList)
  }, [
    strategies,
    employeesData?.employees?.employeeNamesConnection?.nodes,
    teamsData?.teams?.teamsConnection?.edges,
    rolesData?.jobCatalog,
    employeesLoading,
    teamsLoading,
    rolesLoading,
  ])

  const handleDeleteStrategy = useCallback(
    (strategyId: string) => {
      // Quick remove the strategy from the list
      setList((current) => current.filter((item) => item.id !== strategyId))
      // Call the parent component to delete the strategy
      onDeleteStrategy(strategyId)
    },
    [onDeleteStrategy]
  )

  if (!strategies.length) {
    return null
  }

  return (
    <Box gap="s4" width="full">
      {
        // If no resource is loaded
        ((employeesLoading && teamsLoading && rolesLoading) ||
          // or if a new strategy is added but not finish to process
          strategies.length > list.length) && (
          <Box className="ml-2 flex-row gap-2">
            <Skeleton width="s24" height="s24" />
            <Skeleton width="s120" height="s24" />
          </Box>
        )
      }
      {list.map((i) => (
        <ApproverSelection
          key={i.id}
          id={i.id}
          onDelete={handleDeleteStrategy}
          label={i.label}
          avatarUrl={i.avatarSrc}
          avatarFirstName={i.avatarFirstName}
          avatarLastName={i.avatarLastName}
          isDeactivated={i.isDeactivated}
        />
      ))}
    </Box>
  )
}
